import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../ui';
import { useTheme } from '../../theme';

/**
 * The sticky bar above every page: where am I (breadcrumbs), which
 * environment is this (the badge — the one piece of chrome that has saved
 * someone from running a PROD action they meant for DEV), and the two global
 * controls, search and theme.
 */

const RAW_ENV = process.env.REACT_APP_COGNITO_ENV_DISPLAY || '';

export function envKind(raw = RAW_ENV) {
  const v = String(raw).toLowerCase();
  if (v.includes('prod')) return 'prod';
  if (v.includes('stag')) return 'stage';
  if (v.includes('dev') || v.includes('local')) return 'dev';
  return v ? 'other' : null;
}

export function EnvBadge() {
  const kind = envKind();
  if (!kind) return null;
  return (
    <span className="env-badge" data-env={kind} title={`Environment: ${RAW_ENV}`}>
      {RAW_ENV}
    </span>
  );
}

const THEME_META = {
  light: { icon: 'sun', next: 'dark' },
  dark: { icon: 'moon', next: 'system' },
  system: { icon: 'monitor', next: 'light' },
};

export function ThemeToggle({ className = 'topbar-icon-btn' }) {
  const { pref, cycle } = useTheme();
  const meta = THEME_META[pref] || THEME_META.system;
  const label = `Theme: ${pref}. Switch to ${meta.next}`;
  return (
    <button type="button" className={className} onClick={cycle} aria-label={label} title={label}>
      <Icon name={meta.icon} />
    </button>
  );
}

function isMac() {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
}

export default function Topbar({ crumbs, onOpenMenu, onOpenSearch }) {
  return (
    <header className="topbar">
      <div className="topbar-left">
        <button type="button" className="topbar-icon-btn hamburger-btn" onClick={onOpenMenu}
                aria-label="Open navigation">
          <Icon name="menu" />
        </button>
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <ol>
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1;
              return (
                <li key={`${c.label}-${i}`}>
                  {i > 0 && <Icon name="chevronRight" size={14} className="sep" />}
                  {last || !c.to
                    ? <span aria-current={last ? 'page' : undefined}>{c.label}</span>
                    : <Link to={c.to}>{c.label}</Link>}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
      <div className="topbar-right">
        <button type="button" className="topbar-search" onClick={onOpenSearch}
                aria-label="Search and jump to a page" aria-keyshortcuts="Control+K Meta+K">
          <Icon name="search" />
          <span>Search or jump to…</span>
          <kbd className="kbd">{isMac() ? '⌘' : 'Ctrl'} K</kbd>
        </button>
        <ThemeToggle />
        <EnvBadge />
      </div>
    </header>
  );
}
